import React, {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
} from 'react';
import webBiLogger from '@wix/web-bi-logger';
import {
  wixStudioTpPageView,
  wixStudioTpMainAction,
  wixStudioTpGeneralAction,
  wixStudioTpHover,
} from '@wix/bi-logger-generic/v2';
import { useInitialState } from './InitialStateContext';
import { useParamsWithDefaults } from '../hooks/useParamsWithDefaults';
import { useExperiments } from './ExperimentsContext';
import { CATEGORY_GROUPS } from '../config';
import { useTranslation } from '@wix/wix-i18n-config';

type TemplateEventProps = {
  templateId: string;
  title: string;
  index: number;
  slug: string;
};

export interface BILoggerContextValue {
  reportTemplatesPageView: () => void;
  reportEditTemplate: (props: TemplateEventProps) => void;
  reportViewTemplate: (props: TemplateEventProps) => void;
  reportTabClick: (tabName: string) => void;
  reportBrowseSidebar: (button: string) => void;
  reportGeneralAction: (action: string, action_value?: string) => void;
  reportStartFromBlankClick: (isHeader: boolean) => void;
  reportJoinTheWaitlistClick: (isHeader: boolean) => void;
  reportAboveFoldImpression: (props: TemplateEventProps) => void;
}

interface BiLoggerProviderProps {}

const BiLoggerContext = createContext<BILoggerContextValue>({
  reportTemplatesPageView: () => {},
  reportEditTemplate: () => {},
  reportViewTemplate: () => {},
  reportTabClick: () => {},
  reportStartFromBlankClick: () => {},
  reportJoinTheWaitlistClick: () => {},
  reportBrowseSidebar: () => {},
  reportGeneralAction: () => {},
  reportAboveFoldImpression: () => {},
});

const logger = webBiLogger
  .factory({ endpoint: 'generic' })
  .updateDefaults({ src: 2 })
  .logger();
export const BiLoggerProvider: FC<PropsWithChildren<BiLoggerProviderProps>> = ({
  children,
}) => {
  const initialState = useInitialState();
  const { categorySlug } = useParamsWithDefaults();
  const experiments = useExperiments();
  const { t } = useTranslation();
  const isNewStudioTP = experiments.is(
    'specs.funnel.StudioTemplatesPageNewUI',
    'true',
  );

  const getCategoryNameByCategorySlug = useCallback(
    (slug) => {
      return (
        initialState.book?.categories.find((c) => c.slug === slug)
          ?.displayName ?? slug
      );
    },
    [initialState.book?.categories],
  );

  const getBucketNameByCategorySlug = useCallback(
    (slug) => {
      const tKey =
        CATEGORY_GROUPS.find((group) => group.categorySlugs.includes(slug))
          ?.title || 'MISSING_CATEGORY';

      return t(tKey);
    },
    [t],
  );

  const getNewStudioTPProperties = useCallback(() => {
    return (
      (isNewStudioTP && {
        browse_type: getBucketNameByCategorySlug(categorySlug),
        browse_value: getCategoryNameByCategorySlug(categorySlug),
      }) ||
      {}
    );
  }, [
    categorySlug,
    getBucketNameByCategorySlug,
    getCategoryNameByCategorySlug,
    isNewStudioTP,
  ]);

  // evid: 900
  const reportTemplatesPageView = useCallback(() => {
    void logger.report(
      wixStudioTpPageView({
        origin: initialState.referrer,
        tab_name: categorySlug,
        ...getNewStudioTPProperties(),
      }),
    );
  }, [initialState.referrer, categorySlug, getNewStudioTPProperties]);

  // evid: 901
  const reportEditTemplate = useCallback(
    (props: TemplateEventProps) => {
      void logger.report(
        wixStudioTpMainAction({
          action: 'edit',
          template_id: props.templateId,
          origin: initialState.referrer,
          tab_name: categorySlug,
          title: props.title,
          template_index: props.index,
          slug_id: props.slug,
          ...getNewStudioTPProperties(),
        }),
      );
    },
    [initialState.referrer, categorySlug, getNewStudioTPProperties],
  );

  // evid: 901
  const reportViewTemplate = useCallback(
    (props: TemplateEventProps) => {
      void logger.report(
        wixStudioTpMainAction({
          action: 'view',
          template_id: props.templateId,
          origin: initialState.referrer,
          tab_name: categorySlug,
          title: props.title,
          template_index: props.index,
          slug_id: props.slug,
          ...getNewStudioTPProperties(),
        }),
      );
    },
    [initialState.referrer, categorySlug, getNewStudioTPProperties],
  );

  // evid: 902
  // not used in new UI - we don't have tabs in new UI
  const reportTabClick = useCallback(
    (tabName: string) => {
      void logger.report(
        wixStudioTpGeneralAction({
          action: tabName,
          origin: initialState.referrer,
          tab_name: categorySlug,
          is_header: false,
        }),
      );
    },
    [initialState.referrer, categorySlug],
  );

  // evid: 902
  // not used in new UI - the link is different in the footer in new UI
  const reportStartFromBlankClick = useCallback(
    (isHeader: boolean) => {
      void logger.report(
        wixStudioTpGeneralAction({
          action: 'start_from_blank',
          origin: initialState.referrer,
          tab_name: categorySlug,
          is_header: isHeader,
        }),
      );
    },
    [initialState.referrer, categorySlug],
  );

  // evid: 902
  // not used in new UI - new UI uses external header
  const reportJoinTheWaitlistClick = useCallback(
    (isHeader: boolean) => {
      void logger.report(
        wixStudioTpGeneralAction({
          action: 'join_waitlist',
          origin: initialState.referrer,
          tab_name: categorySlug,
          is_header: isHeader,
        }),
      );
    },
    [initialState.referrer, categorySlug],
  );

  // evid: 902
  const reportBrowseSidebar = useCallback(
    (button: string) => {
      void logger.report(
        wixStudioTpGeneralAction({
          action: 'sidebar_browse',
          action_value: button,
          ...getNewStudioTPProperties(),
        }),
      );
    },
    [getNewStudioTPProperties],
  );

  // evid: 902
  const reportGeneralAction = useCallback(
    (action: string, action_value?: string) => {
      void logger.report(
        wixStudioTpGeneralAction({
          action,
          action_value,
          ...getNewStudioTPProperties(),
        }),
      );
    },
    [getNewStudioTPProperties],
  );

  // evid: 903
  const reportAboveFoldImpression = useCallback(
    (props: TemplateEventProps) => {
      void logger.report(
        wixStudioTpHover({
          ...getNewStudioTPProperties(),
          origin: initialState.referrer,
          slug_id: props.slug,
          tab_name: categorySlug,
          template_id: props.templateId,
          template_index: props.index,
          title: props.title,
          window_height: window.innerHeight,
          window_width: window.innerWidth,
        }),
      );
    },
    [categorySlug, getNewStudioTPProperties, initialState.referrer],
  );

  return (
    <BiLoggerContext.Provider
      value={{
        reportTemplatesPageView,
        reportEditTemplate,
        reportViewTemplate,
        reportTabClick,
        reportStartFromBlankClick,
        reportJoinTheWaitlistClick,
        reportBrowseSidebar,
        reportGeneralAction,
        reportAboveFoldImpression,
      }}
    >
      {children}
    </BiLoggerContext.Provider>
  );
};

export const useBiLogger = () => {
  const context = React.useContext(BiLoggerContext);
  if (!context) {
    throw new Error('useBiLogger must be used within a BiLoggerProvider');
  }

  return context;
};
